import { UseFormReturn } from 'react-hook-form'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface RepresentationAppellateFormProps {
  form: UseFormReturn<any>
  isView?: boolean
  courts?: Array<{ court: string; code: string }>
}

export function RepresentationAppellateForm({
  form,
  isView,
  courts = [],
}: RepresentationAppellateFormProps) {
  const caseType = form.watch('Case Type')
  const displayCaseType =
    caseType === 'criminal_case_appellate'
      ? 'Criminal Case'
      : caseType === 'civil_case_appellate'
      ? 'Civil Case'
      : 'Labor Case'

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-end justify-between border-b-2 border-primary/20 pb-4">
        <div className="bg-primary text-white px-6 py-3 rounded-full font-bold text-lg">
          {displayCaseType}
        </div>

        <FormField
          control={form.control}
          name="Court Code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Court Code *</FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(value)
                  // Auto-populate Court Name
                  const court = courts.find((c) => c.code === value)
                  if (court) {
                    form.setValue('Court Name', court.court)
                  }
                }}
                value={field.value}
                disabled={isView}
              >
                <FormControl>
                  <SelectTrigger className="w-[285px]">
                    <SelectValue placeholder="Select court code" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {courts.length > 0 ? (
                    courts.map(({ court, code }) => (
                      <SelectItem key={code} value={code}>
                        {court}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="none">No courts available</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <input type="hidden" {...form.register('Court Name')} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="Date Of Confinement"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Date of Confinement *</FormLabel>
                <FormControl>
                  <Input {...field} type="date" disabled={isView} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Place of Detention"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Place of Detention *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter place of detention" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Case Title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Case Title *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter case title" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Docket Number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Docket Number *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter docket number" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Adverse Party"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Adverse Party *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter adverse party" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Court of Origin"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Court of Origin *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter court of origin" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Court of Origin Handling Lawyer"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Court of Origin Handling Lawyer *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter handling lawyer" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="Nature Of Offense"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nature of Offense *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter nature of offense" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Court Pending"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Court Pending *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter court pending" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Name Of Adverse Counsel"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name of Adverse Counsel *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter adverse counsel name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Action Taken"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Action Taken *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter action taken" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Address Of Adverse Party"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address of Adverse Party *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter address" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Representative Of PDL"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Representative of PDL *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isView} placeholder="Enter representative" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="Contact Number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contact Number *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={isView}
                    placeholder="09XXXXXXXXX"
                    pattern="^09\d{9}$"
                    title="Format: 09XXXXXXXXX - X being a number"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  )
}
